import possibleTypes from "@/__generated__/possibleTypes.json";
import { app } from "@/config/env";
import { ApolloClient, ApolloLink, HttpLink, type ServerError } from "@apollo/client";
import { InMemoryCache, type NormalizedCacheObject } from "@apollo/client/cache";
import { onError, type ErrorLink } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";

import { createExpectedErrorsLink, isExpectedError } from "@luce/core/apollo";

import { Sentry } from "../monitoring/sentry";
import { tokenManager } from "../token-manager";
import { createFilteredEntityTypePolicy } from "./cache";

function initClientInstance(): ApolloClient<NormalizedCacheObject> {
  const httpLink: HttpLink = new HttpLink({
    uri: app.PUBLIC_API_ENDPOINT,
  });

  const uploadLink = createUploadLink({
    uri: app.PUBLIC_API_ENDPOINT,
  });

  const link = ApolloLink.split(
    (operation) => !!operation.getContext().hasUpload,
    uploadLink,
    httpLink,
  );

  const onErrorCallback: ErrorLink.ErrorHandler = ({ graphQLErrors, networkError, operation }) => {
    const serverError = networkError as ServerError;
    if ([503, 504].includes(serverError?.statusCode)) {
      console.log(`[Maintenance Mode]: Server current on status ${serverError.response.status}`);
      return;
    }

    if (serverError?.message === "Failed to fetch") {
      return;
    }

    const statusCode = serverError?.statusCode;
    if (statusCode === 204 || statusCode >= 400) {
      // server error
      console.log(`[Network error]: ${serverError.response.status}`);
    }

    if (graphQLErrors?.length === 0) {
      // no errors
      return;
    }

    const operationName = operation.operationName || "unknown_operation";
    const variables = JSON.stringify(operation.variables);

    for (const error of graphQLErrors ?? []) {
      const { message, locations, path } = error;
      const m = `Message: ${message}`;
      const l = `Location: ${JSON.stringify(locations)}`;
      const p = `Path: ${JSON.stringify(path)}`;
      console.log(`[GraphQL error]: ${m}, ${l}, ${p}`);

      // skip required confirmations from sentry reporting
      if (error.extensions?.required_confirmations) {
        continue;
      }

      // skip expected (user-facing) errors promoted by the expectedErrors
      // bridge — these are reported by the BE with a traceId, not FE bugs.
      if (isExpectedError(error)) {
        continue;
      }

      Sentry.withScope((scope) => {
        scope.setTag("graphql.operation", operationName);
        scope.setFingerprint(["graphql", operationName]);
        Sentry.captureException(new Error(message), {
          extra: {
            operationName,
            variables,
          },
        });
      });
    }
  };

  const errorLink = onError(onErrorCallback);

  // https://www.apollographql.com/docs/react/data/fragments/#generating-possibletypes-automatically
  const cache = new InMemoryCache({
    possibleTypes,
    typePolicies: {
      FreeTimeslot: {
        keyFields: ["id", "startAt", "endAt"],
      },
      Query: {
        fields: {
          packagesByFilters: createFilteredEntityTypePolicy("packages", ["filters", ["status"]]),
          clientNotificationByFilters: createFilteredEntityTypePolicy("notifications", [
            "filters",
            ["isRead"],
          ]),
          visitsByFilters: createFilteredEntityTypePolicy(
            "visits",
            ["filters", ["clientId", "from", "to", "status", "limit", "packageId"]], // separate cache per filter combo
          ),
          invoicesByFilters: createFilteredEntityTypePolicy("invoices", [
            "filters",
            ["clientId", "status", "sortBy", "sort", "paymentStatus"],
          ]),
        },
      },
    },
  });

  return new ApolloClient({
    connectToDevTools: app.ENV !== "production",
    link: ApolloLink.from([
      tokenManager.authHeaderLink(),
      errorLink,
      tokenManager.errorLink(),
      // Promotes BE `expectedErrors` → top-level `errors[]` so existing catch
      // blocks handle them. Sits closest to the network: responses unwind
      // right-to-left, so it runs before errorLink, which then sees the
      // promoted errors (with the `expectedError` marker) and skips reporting.
      createExpectedErrorsLink(),
      link,
    ]),
    cache,
  });
}

export const ClientInstance = initClientInstance();
